import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";

const description = `Creates a comprehensive recipe based on the successful completion of a task. This tool generates detailed instructions that include the endpoints, API paths, and implementation steps that led to successfully completing the task.

Use this tool when:
- A user says "Create a recipe for this tool" or similar
- You've successfully completed a complex task and want to document the process
- You want to capture the successful implementation pattern for future use

The generated recipe will include:
- Task description and context
- Successful API endpoints and paths used
- Step-by-step implementation details
- Code patterns and configurations
- Troubleshooting tips from the actual execution

This recipe can then be copied and added to the codebase where recipes are stored for future reference.`;

const parameters = z.object({
  taskDescription: z
    .string()
    .describe("Description of the task that was successfully completed"),
  assetType: z
    .string()
    .describe(
      "The type of asset that was created or worked on (e.g., 'Zap', 'Table', 'Agent', 'Interface')",
    ),
  successfulEndpoints: z
    .array(
      z.object({
        api: z.string().describe("The API that was used"),
        path: z.string().describe("The path that was called"),
        method: z.string().describe("HTTP method used"),
        purpose: z.string().describe("What this endpoint was used for"),
      }),
    )
    .describe("List of API endpoints that were successfully used"),
  implementationSteps: z
    .array(z.string())
    .describe("Step-by-step process that led to success"),
  keyPatterns: z
    .array(z.string())
    .optional()
    .describe("Important code patterns or configurations used"),
  troubleshootingNotes: z
    .array(z.string())
    .optional()
    .describe("Any issues encountered and how they were resolved"),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short message describing what you're doing, like 'Generating recipe...'",
    ),
});

export const createRecipeAtom = atom<JotaiTool<z.infer<typeof parameters>>>(
  () => ({
    name: "create_recipe",
    description: description,
    parameters: parameters,
    execute: atom(null, async (_get, _set, { args }) => {
      try {
        const {
          taskDescription,
          assetType,
          successfulEndpoints,
          implementationSteps,
          keyPatterns = [],
          troubleshootingNotes = [],
        } = args;

        // Generate a unique recipe ID based on the task
        const recipeId = `${assetType.toLowerCase()}_${taskDescription
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "_")
          .substring(0, 30)}`;

        // Create the recipe content
        const recipeContent = {
          id: recipeId,
          title: `${assetType}: ${taskDescription}`,
          description: `Recipe for successfully ${taskDescription.toLowerCase()} for ${assetType}`,
          category: assetType.toLowerCase(),
          difficulty: "intermediate",
          estimatedTime: "15-30 minutes",

          overview: {
            taskDescription,
            assetType,
            successCriteria:
              "Task completed successfully with all functionality working as expected",
          },

          prerequisites: [
            "Access to Canvas development environment",
            "Appropriate permissions for the target APIs",
            "Understanding of the Canvas copilot tool system",
          ],

          apiEndpoints: successfulEndpoints.map((endpoint) => ({
            api: endpoint.api,
            path: endpoint.path,
            method: endpoint.method,
            purpose: endpoint.purpose,
            authentication: "Uses session cookies via network_request tool",
            example: `network_request({ method: "${endpoint.method}", api: "${endpoint.api}", path: "${endpoint.path}" })`,
          })),

          implementationSteps: implementationSteps.map((step, index) => ({
            step: index + 1,
            title: step,
            details: step,
            toolsUsed:
              step.toLowerCase().includes("api") ||
              step.toLowerCase().includes("request")
                ? ["network_request"]
                : step.toLowerCase().includes("search")
                  ? ["search_tool", "search_recipes_tool"]
                  : [],
          })),

          codePatterns:
            keyPatterns.length > 0
              ? keyPatterns.map((pattern) => ({
                  title: "Key Implementation Pattern",
                  code: pattern,
                  explanation:
                    "This pattern was essential for successful completion",
                }))
              : [],

          troubleshooting:
            troubleshootingNotes.length > 0
              ? troubleshootingNotes.map((note) => ({
                  issue: note,
                  solution: "Resolved during successful implementation",
                  prevention:
                    "Follow the exact steps and API calls documented in this recipe",
                }))
              : [],

          validation: {
            steps: [
              "Verify all API calls return successful responses",
              "Check that the created/modified asset functions as expected",
              "Test the implementation in the Canvas environment",
            ],
            expectedOutcomes: [
              "Asset is created or modified successfully",
              "All functionality works as intended",
              "No errors in console or API responses",
            ],
          },

          metadata: {
            createdAt: new Date().toISOString(),
            source: "Generated from successful task completion",
            version: "1.0.0",
          },
        };

        const formattedRecipe = `
# Recipe: ${recipeContent.title}

## Overview
- **Task**: ${recipeContent.overview.taskDescription}
- **Asset Type**: ${recipeContent.overview.assetType}
- **Category**: ${recipeContent.category}
- **Difficulty**: ${recipeContent.difficulty}
- **Estimated Time**: ${recipeContent.estimatedTime}

## Prerequisites
${recipeContent.prerequisites.map((req) => `- ${req}`).join("\n")}

## API Endpoints Used
${recipeContent.apiEndpoints
  .map(
    (endpoint) => `
### ${endpoint.method} ${endpoint.api}${endpoint.path}
- **Purpose**: ${endpoint.purpose}
- **Authentication**: ${endpoint.authentication}
- **Example**: \`${endpoint.example}\`
`,
  )
  .join("\n")}

## Implementation Steps
${recipeContent.implementationSteps
  .map(
    (step) => `
${step.step}. **${step.title}**
   - Details: ${step.details}
   ${step.toolsUsed.length > 0 ? `- Tools: ${step.toolsUsed.join(", ")}` : ""}
`,
  )
  .join("\n")}

${
  recipeContent.codePatterns.length > 0
    ? `
## Key Code Patterns
${recipeContent.codePatterns
  .map(
    (pattern) => `
### ${pattern.title}
\`\`\`
${pattern.code}
\`\`\`
${pattern.explanation}
`,
  )
  .join("\n")}`
    : ""
}

${
  recipeContent.troubleshooting.length > 0
    ? `
## Troubleshooting
${recipeContent.troubleshooting
  .map(
    (item) => `
### Issue: ${item.issue}
- **Solution**: ${item.solution}
- **Prevention**: ${item.prevention}
`,
  )
  .join("\n")}`
    : ""
}

## Validation
### Steps to Verify Success
${recipeContent.validation.steps.map((step) => `- ${step}`).join("\n")}

### Expected Outcomes
${recipeContent.validation.expectedOutcomes.map((outcome) => `- ${outcome}`).join("\n")}

## Recipe Metadata
- **Recipe ID**: ${recipeContent.id}
- **Created**: ${recipeContent.metadata.createdAt}
- **Source**: ${recipeContent.metadata.source}
- **Version**: ${recipeContent.metadata.version}

---

**Instructions for Use:**
1. Copy this entire recipe content
2. Add it to the recipe definitions in the codebase at:
   \`packages/copilot-tools/src/tools/search_recipes_tool/recipes/recipe_definitions_full.ts\`
3. Update the recipe index to include this new recipe
4. The recipe will then be searchable and usable by the copilot system
`;
        // console.log(formattedRecipe);
        return {
          content: [
            {
              type: "text",
              text: `Recipe created successfully! Here's the complete recipe that documents the successful implementation:\n\n${formattedRecipe}`,
            },
          ],
        };
      } catch (error) {
        console.error("[CREATE_RECIPE] Error creating recipe:", error);
        return {
          $error: {
            message: `Error creating recipe: ${error instanceof Error ? error.message : "Unknown error"}`,
          },
        };
      }
    }),
  }),
);

createRecipeAtom.debugLabel = "createRecipeAtom";
